/**
 * GA4 Custom Events - Refurbished Miller
 * Copyright 2026, Interioshops BV
 * 
 * Handles: add_to_cart, remove_from_cart, begin_checkout, select_item
 * Prijzen zijn INCLUSIEF BTW (B2C shop)
 */

(function() {
  'use strict';
  
  // Ensure dataLayer exists
  window.dataLayer = window.dataLayer || [];

  // =============================================================================
  // GLOBO PRODUCT OPTIONS + TAXIFY PRICE SYNC
  // =============================================================================
  // Globo Product Options updates prices when add-on options are selected.
  // Taxify shows excl/incl VAT prices in its own container.
  // This code syncs Taxify prices when Globo updates the product price.
  // 
  // Taxify structure:
  // <div class="taxify-price-container">
  //   <span class="taxify-price taxify-price--exclusive">
  //     <span class="taxify-amount">€570,00</span>
  //     <span class="taxify-text-after">Excl. btw</span>
  //   </span>
  //   <span class="taxify-price taxify-price--inclusive">
  //     <span class="taxify-amount">€689,70</span>
  //     <span class="taxify-text-after">Incl. btw</span>
  //   </span>
  // </div>
  // =============================================================================
  
  /**
   * Log a debug message, only when ?debug=taxify is in the URL
   */
  function debugLog(...args) {
    if (new URLSearchParams(location.search).get('debug') === 'taxify') {
      console.log(...args);
    }
  }
  
  /**
   * Parse a price string to cents (integer)
   * Handles Dutch format: €1.234,56 or 1234,56 or €613,00 EUR
   * Also handles text like "Aanbiedingsprijs€599,00"
   */
  function parsePriceToCents(priceStr) {
    if (!priceStr) return 0;
    
    // Extract the price using regex - find €XXX,XX or just XXX,XX pattern
    const priceMatch = priceStr.match(/€\s*([\d.,]+)|(\d{1,3}(?:[.,]\d{3})*(?:[.,]\d{2}))/);
    if (!priceMatch) return 0;
    
    let cleaned = (priceMatch[1] || priceMatch[2] || '').trim();
    
    // Dutch format uses dot as thousands separator and comma as decimal
    if (cleaned.includes(',')) {
      // Remove dots (thousands separator), replace comma with dot
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    }
    
    const value = parseFloat(cleaned);
    return isNaN(value) ? 0 : Math.round(value * 100);
  }
  
  /**
   * Format cents to price string (Dutch format: €570,00)
   */
  function formatPrice(cents) {
    const euros = cents / 100;
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(euros);
  }
  
  /**
   * Get the stored VAT rate (calculated once on page load from Taxify's initial prices)
   */
  let _storedVatRate = null;
  
  function getVatRate() {
    if (_storedVatRate !== null) return _storedVatRate;
    
    const container = document.querySelector('.taxify-price-container');
    if (container) {
      const exclAmount = container.querySelector('.taxify-price--exclusive .taxify-amount');
      const inclAmount = container.querySelector('.taxify-price--inclusive .taxify-amount');
      
      if (exclAmount && inclAmount) {
        const exclCents = parsePriceToCents(exclAmount.textContent);
        const inclCents = parsePriceToCents(inclAmount.textContent);
        if (exclCents > 0 && inclCents > 0) {
          _storedVatRate = inclCents / exclCents;
          return _storedVatRate;
        }
      }
    }
    _storedVatRate = 1.21; // Default 21% BTW (Netherlands)
    return _storedVatRate;
  }
  
  /**
   * Update Taxify prices based on a new base price
   * @param {number} newPriceCents - The new price in cents
   * @param {boolean} priceIsExcl - Whether the new price is excl. VAT (true) or incl. VAT (false)
   */
  let _isUpdatingTaxify = false;
  
  function updateTaxifyPrices(newPriceCents, priceIsExcl = true) {
    if (_isUpdatingTaxify || newPriceCents <= 0) return;
    _isUpdatingTaxify = true;
    
    try {
      const vatRate = getVatRate();
      
      let exclCents, inclCents;
      if (priceIsExcl) {
        exclCents = newPriceCents;
        inclCents = Math.round(newPriceCents * vatRate);
      } else {
        inclCents = newPriceCents;
        exclCents = Math.round(newPriceCents / vatRate);
      }
      
      // Update all Taxify containers on the page
      document.querySelectorAll('.taxify-price-container').forEach(container => {
        const exclAmountEl = container.querySelector('.taxify-price--exclusive .taxify-amount');
        const inclAmountEl = container.querySelector('.taxify-price--inclusive .taxify-amount');
        
        if (exclAmountEl) {
          exclAmountEl.textContent = formatPrice(exclCents);
        }
        if (inclAmountEl) {
          inclAmountEl.textContent = formatPrice(inclCents);
        }
      });
      
      debugLog('[Globo+Taxify] Updated prices - Excl:', formatPrice(exclCents), 'Incl:', formatPrice(inclCents));
    } finally {
      setTimeout(function() { _isUpdatingTaxify = false; }, 100);
    }
  }
  
  /**
   * Read the current price from the hidden original sale-price or Globo's display
   */
  function getCurrentGloboPrice() {
    // Get the BASE price from the hidden sale-price (without addons)
    let basePriceCents = 0;
    const hiddenSalePrice = document.querySelector('sale-price.taxify-original-hidden, sale-price[data-taxify-processed]');
    if (hiddenSalePrice) {
      basePriceCents = parsePriceToCents(hiddenSalePrice.textContent);
    }
    
    if (basePriceCents === 0) {
      // Fallback: try the Taxify excl price
      const taxifyExcl = document.querySelector('.taxify-price--exclusive .taxify-amount');
      if (taxifyExcl) {
        basePriceCents = parsePriceToCents(taxifyExcl.textContent);
      }
    }
    
    debugLog('[Globo+Taxify] Base price:', basePriceCents, 'cents');
    
    // Now add up all selected ADDON prices from Globo options
    let addonTotalCents = 0;
    const selectedOptions = document.querySelectorAll('.gpo-swatches input[type="radio"]:checked, .gpo-swatches input[type="checkbox"]:checked');
    
    selectedOptions.forEach(input => {
      // Find the addon price in the label
      const wrapper = input.closest('.gpo-swatches');
      if (wrapper) {
        const addonSpan = wrapper.querySelector('.addOn');
        if (addonSpan) {
          const addonText = addonSpan.textContent; // e.g., "(+ €19,00 EUR)"
          const addonCents = parsePriceToCents(addonText);
          debugLog('[Globo+Taxify] Addon found:', addonText, '=', addonCents, 'cents');
          addonTotalCents += addonCents;
        }
      }
    });
    
    debugLog('[Globo+Taxify] Total addons:', addonTotalCents, 'cents');
    debugLog('[Globo+Taxify] Total price:', basePriceCents + addonTotalCents, 'cents');
    
    return basePriceCents + addonTotalCents;
  }
  
  // Store the initial base price (before any addons)
  let _basePriceCents = 0;
  
  function getBasePriceCents() {
    if (_basePriceCents > 0) return _basePriceCents;
    
    // Get from hidden sale-price
    const hiddenSalePrice = document.querySelector('sale-price.taxify-original-hidden, sale-price[data-taxify-processed]');
    if (hiddenSalePrice) {
      _basePriceCents = parsePriceToCents(hiddenSalePrice.textContent);
    }
    
    // Fallback: get from Taxify's initial excl price
    if (_basePriceCents === 0) {
      const taxifyExcl = document.querySelector('.taxify-price--exclusive .taxify-amount');
      if (taxifyExcl) {
        _basePriceCents = parsePriceToCents(taxifyExcl.textContent);
      }
    }
    
    return _basePriceCents;
  }
  
  /**
   * Calculate total price from base + selected addons
   */
  function calculateTotalPrice() {
    const baseCents = getBasePriceCents();
    let addonTotalCents = 0;
    
    // Find all selected Globo options and sum their addon prices
    const selectedOptions = document.querySelectorAll('.gpo-swatches input[type="radio"]:checked, .gpo-swatches input[type="checkbox"]:checked');
    
    selectedOptions.forEach(input => {
      const wrapper = input.closest('.gpo-swatches');
      if (wrapper) {
        const addonSpan = wrapper.querySelector('.addOn');
        if (addonSpan) {
          const addonCents = parsePriceToCents(addonSpan.textContent);
          addonTotalCents += addonCents;
        }
      }
    });
    
    debugLog('[Globo+Taxify] Calculated total: base', baseCents, '+ addons', addonTotalCents, '=', baseCents + addonTotalCents);
    return baseCents + addonTotalCents;
  }
  
  /**
   * Set up Globo + Taxify price sync once the Taxify container is available
   */
  function setupGloboTaxifySync(taxifyContainer) {
    // Store the initial VAT rate
    getVatRate();
    
    debugLog('[Globo+Taxify] Initialized! VAT rate:', _storedVatRate);
    debugLog('[Globo+Taxify] Taxify container found:', taxifyContainer);
    
    // Method 1: Listen for Globo's custom events
    ['gpo:price:changed', 'gpo:variant:changed', 'globoProductOptions.priceUpdated', 
     'gpo:total:updated', 'gpo-price-updated'].forEach(eventName => {
      document.addEventListener(eventName, function(e) {
        debugLog('[Globo+Taxify] Event received:', eventName, e.detail);
        if (e.detail && typeof e.detail.price !== 'undefined') {
          updateTaxifyPrices(Math.round(e.detail.price * 100), true);
        } else if (e.detail && typeof e.detail.total !== 'undefined') {
          updateTaxifyPrices(Math.round(e.detail.total * 100), true);
        } else {
          // Fallback: try to read the price from DOM
          const price = getCurrentGloboPrice();
          if (price > 0) updateTaxifyPrices(price, true);
        }
      });
    });
    
    // Method 2: Listen for changes on Globo option inputs
    document.addEventListener('change', function(e) {
      const target = e.target;
      const isGloboElement = target.closest('.gpo-swatches, .gpo-element, .gpo-group');
      
      if (isGloboElement) {
        debugLog('[Globo+Taxify] Globo option changed');
        // Small delay to ensure DOM is updated
        setTimeout(function() {
          const price = calculateTotalPrice();
          if (price > 0) {
            updateTaxifyPrices(price, true);
          }
        }, 50);
      }
    });
    
    // Method 3: Listen for clicks on Globo options (radio buttons, checkboxes, swatches)
    document.addEventListener('click', function(e) {
      const target = e.target;
      const isGloboElement = target.closest('.gpo-swatches, .gpo-element, .gpo-ov-item');
      
      if (isGloboElement) {
        debugLog('[Globo+Taxify] Globo option clicked');
        setTimeout(function() {
          const price = calculateTotalPrice();
          if (price > 0) {
            updateTaxifyPrices(price, true);
          }
        }, 100);
      }
    });
    
    // Method 4: Watch the hidden sale-price element for changes (Globo may update it)
    const hiddenSalePrice = document.querySelector('sale-price.taxify-original-hidden, sale-price[data-taxify-processed]');
    if (hiddenSalePrice) {
      debugLog('[Globo+Taxify] Watching hidden sale-price:', hiddenSalePrice.textContent);
      const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.type === 'characterData' || mutation.type === 'childList') {
            const newPrice = parsePriceToCents(hiddenSalePrice.textContent);
            if (newPrice > 0) {
              debugLog('[Globo+Taxify] Hidden sale-price updated:', hiddenSalePrice.textContent);
              updateTaxifyPrices(newPrice, true);
            }
          }
        });
      });
      
      observer.observe(hiddenSalePrice, {
        characterData: true,
        childList: true,
        subtree: true
      });
    }
  }
  
  // Initialize when DOM is ready; on a product page wait for Taxify (max 15s), on other pages do nothing
  let globoTaxifyStarted = false;
  
  function initGloboTaxifySync() {
    // Only run on a product page
    if (!document.querySelector('.shopify-section--main-product')) return;
    
    // Guarantee the initialisation runs at most once
    if (globoTaxifyStarted) return;
    globoTaxifyStarted = true;
    
    debugLog('[Globo+Taxify] Product page detected, starting init...');
    
    // Check if Taxify has already loaded
    const taxifyContainer = document.querySelector('.taxify-price-container');
    
    if (taxifyContainer) {
      debugLog('[Globo+Taxify] Taxify container found, starting setup...');
      setupGloboTaxifySync(taxifyContainer);
      return;
    }
    
    debugLog('[Globo+Taxify] Taxify container not found yet, waiting with MutationObserver...');
    
    let observer;
    
    // Stop waiting after 15 seconds (Taxify may not be installed)
    const timeout = setTimeout(function() {
      if (observer) observer.disconnect();
      debugLog('[Globo+Taxify] Taxify container not found within 15 seconds, giving up.');
    }, 15000);
    
    observer = new MutationObserver(function() {
      const container = document.querySelector('.taxify-price-container');
      
      if (container) {
        observer.disconnect();
        clearTimeout(timeout);
        debugLog('[Globo+Taxify] Taxify container found, starting setup...');
        setupGloboTaxifySync(container);
      }
    });
    
    observer.observe(document.body, { childList: true, subtree: true });
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      debugLog('[Globo+Taxify] DOM loaded, starting init...');
      initGloboTaxifySync();
    });
  } else {
    debugLog('[Globo+Taxify] DOM already loaded, starting init...');
    initGloboTaxifySync();
  }

  // =============================================================================
  // TRUSTPILOT TRUSTBOX - LABELS IN THE STOREFRONT LANGUAGE
  // =============================================================================
  // The TrustBox widgets are pasted in the templates with data-locale="en-US". The locale only sets
  // the widget labels ("Great" / "Goed"); the reviews themselves are shown in all languages either
  // way. custom.js is deferred, so this normally runs before the Trustpilot bootstrap initialises the
  // widgets on DOMContentLoaded; a widget that is already initialised is reloaded.
  // =============================================================================

  const TRUSTPILOT_LOCALES = {
    en: 'en-US',
    nl: 'nl-NL',
    de: 'de-DE',
    fr: 'fr-FR',
    it: 'it-IT',
    es: 'es-ES',
    pl: 'pl-PL'
  };

  function setTrustpilotLocale() {
    const language = (document.documentElement.lang || '').split('-')[0].toLowerCase();
    const locale = TRUSTPILOT_LOCALES[language];
    if (!locale) return;

    document.querySelectorAll('.trustpilot-widget').forEach(function(widget) {
      if (widget.getAttribute('data-locale') === locale) return;
      widget.setAttribute('data-locale', locale);

      if (widget.querySelector('iframe') && window.Trustpilot && typeof window.Trustpilot.loadFromElement === 'function') {
        window.Trustpilot.loadFromElement(widget, true);
      }
    });
  }

  setTrustpilotLocale();

  /**
   * Extract product data from a product card element
   */
  function getProductDataFromCard(card) {
    const productId = card.getAttribute('data-product-id') || 
                      card.querySelector('[data-product-id]')?.getAttribute('data-product-id');
    const productHandle = card.getAttribute('handle') || 
                          card.querySelector('a[href*="/products/"]')?.href?.match(/\/products\/([^/?]+)/)?.[1];
    
    return {
      item_id: productId || productHandle || 'unknown',
      item_name: card.querySelector('.product-card__title, .product-title, h3, h2')?.textContent?.trim() || 'Unknown Product',
      item_brand: card.querySelector('.product-card__vendor, .vendor')?.textContent?.trim() || '',
      price: parseFloat(card.querySelector('.price, .product-card__price')?.textContent?.replace(/[^\d,.-]/g, '').replace(',', '.')) || 0
    };
  }
  
  /**
   * Get product data from product page (for add to cart on product page)
   */
  function getProductDataFromPage() {
    // Try to get data from meta tags or JSON-LD
    const productMeta = document.querySelector('meta[property="og:title"]');
    const priceMeta = document.querySelector('meta[property="product:price:amount"]');
    const productJson = document.querySelector('[data-product-json], script[type="application/ld+json"]');
    
    let productData = {
      item_id: '',
      item_name: '',
      item_brand: '',
      price: 0,
      quantity: 1
    };
    
    // Try JSON-LD first
    if (productJson) {
      try {
        const json = JSON.parse(productJson.textContent);
        if (json['@type'] === 'Product') {
          productData.item_id = json.productID || json.sku || '';
          productData.item_name = json.name || '';
          productData.item_brand = json.brand?.name || '';
          productData.price = parseFloat(json.offers?.price) || 0;
        }
      } catch (e) {}
    }
    
    // Fallback to meta tags
    if (!productData.item_name && productMeta) {
      productData.item_name = productMeta.content;
    }
    if (!productData.price && priceMeta) {
      productData.price = parseFloat(priceMeta.content) || 0;
    }
    
    // Get quantity from form
    const quantityInput = document.querySelector('input[name="quantity"], .quantity-input input');
    if (quantityInput) {
      productData.quantity = parseInt(quantityInput.value) || 1;
    }
    
    // Get variant ID from form
    const variantInput = document.querySelector('input[name="id"], select[name="id"]');
    if (variantInput) {
      productData.item_variant = variantInput.value;
    }
    
    return productData;
  }
  
  /**
   * Push add_to_cart event
   */
  function pushAddToCart(productData) {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      'event': 'add_to_cart',
      'ecommerce': {
        'currency': window.Shopify?.currency?.active || 'EUR',
        'value': productData.price * (productData.quantity || 1),
        'items': [{
          'item_id': productData.item_id,
          'item_name': productData.item_name,
          'item_brand': productData.item_brand || '',
          'item_variant': productData.item_variant || '',
          'price': productData.price,
          'quantity': productData.quantity || 1
        }]
      }
    });
  }
  
  /**
   * Push remove_from_cart event
   */
  function pushRemoveFromCart(productData) {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      'event': 'remove_from_cart',
      'ecommerce': {
        'currency': window.Shopify?.currency?.active || 'EUR',
        'value': productData.price * (productData.quantity || 1),
        'items': [{
          'item_id': productData.item_id,
          'item_name': productData.item_name,
          'item_brand': productData.item_brand || '',
          'price': productData.price,
          'quantity': productData.quantity || 1
        }]
      }
    });
  }
  
  /**
   * Push select_item event (product click in collection)
   */
  function pushSelectItem(productData, listId, listName) {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      'event': 'select_item',
      'ecommerce': {
        'item_list_id': listId || 'collection',
        'item_list_name': listName || 'Collection',
        'items': [{
          'item_id': productData.item_id,
          'item_name': productData.item_name,
          'item_brand': productData.item_brand || '',
          'price': productData.price
        }]
      }
    });
  }
  
  /**
   * Push begin_checkout event
   */
  function pushBeginCheckout() {
    // Fetch current cart data
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const items = cart.items.map((item, index) => ({
          'item_id': item.product_id,
          'item_name': item.product_title,
          'item_brand': item.vendor || '',
          'item_variant': item.variant_id,
          'price': item.final_price / 100,
          'quantity': item.quantity
        }));
        
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          'event': 'begin_checkout',
          'ecommerce': {
            'currency': cart.currency || window.Shopify?.currency?.active || 'EUR',
            'value': cart.total_price / 100,
            'items': items
          }
        });
      })
      .catch(err => {});
  }
  
  // ============================================
  // EVENT LISTENERS
  // ============================================
  
  document.addEventListener('DOMContentLoaded', function() {
    
    // 1. Remove from Cart - Listen for quantity changes to 0 or remove clicks
    document.addEventListener('click', function(e) {
      const removeBtn = e.target.closest('[data-cart-remove], .cart-item__remove, button[name="minus"]');
      if (removeBtn) {
        const lineItem = removeBtn.closest('.cart-item, .line-item, [data-line-item]');
        if (lineItem) {
          const productData = {
            item_id: lineItem.getAttribute('data-product-id') || '',
            item_name: lineItem.querySelector('.cart-item__title, .line-item__title, a')?.textContent?.trim() || '',
            price: parseFloat(lineItem.querySelector('.cart-item__price, .price')?.textContent?.replace(/[^\d,.-]/g, '').replace(',', '.')) || 0,
            quantity: 1
          };
          // Only push if it's an actual remove (not just decrease)
          if (removeBtn.matches('[data-cart-remove], .cart-item__remove')) {
            pushRemoveFromCart(productData);
          }
        }
      }
    });
    
    // 2. Select Item - Product card clicks
    document.addEventListener('click', function(e) {
      const productCard = e.target.closest('product-card, .product-card');
      const link = e.target.closest('a[href*="/products/"]');
      
      if (productCard && link) {
        const productData = getProductDataFromCard(productCard);
        const collection = document.querySelector('.collection, [data-collection-handle]');
        const listId = collection?.getAttribute('data-collection-handle') || 'collection';
        const listName = document.querySelector('.collection__title, h1')?.textContent?.trim() || 'Collection';
        pushSelectItem(productData, listId, listName);
      }
    });
    
    // 3. Begin Checkout - Checkout button clicks
    document.addEventListener('click', function(e) {
      const checkoutBtn = e.target.closest('[name="checkout"], .checkout-button, a[href*="/checkout"]');
      if (checkoutBtn) {
        pushBeginCheckout();
      }
    });
    
  });
  
  // Listen for Shopify cart AJAX events (fallback)
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    const url = args[0];
    
    return originalFetch.apply(this, args).then(response => {
      // Clone the response so we can read it
      const clonedResponse = response.clone();
      
      // Check for cart/add
      if (typeof url === 'string' && url.includes('/cart/add')) {
        clonedResponse.json().then(data => {
          if (data && data.items) {
            data.items.forEach(item => {
              pushAddToCart({
                item_id: item.product_id,
                item_name: item.product_title,
                item_brand: item.vendor || '',
                item_variant: item.variant_id,
                price: item.final_price / 100,
                quantity: item.quantity
              });
            });
          } else if (data && data.product_id) {
            pushAddToCart({
              item_id: data.product_id,
              item_name: data.product_title,
              item_brand: data.vendor || '',
              item_variant: data.variant_id,
              price: data.final_price / 100,
              quantity: data.quantity
            });
          }
        }).catch(() => {});
      }
      
      return response;
    });
  };
  
})();
